import React from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface Props {
  accountList: any[];
  isListClients?: boolean;
}

const BASE_COLUMNS = [
  "User Name",
  "Credit Reference",
  "U St",
  "B St",
  "Exposure Limit",
  "Default (%)",
  "Account Type",
];

const EXTENDED_COLUMNS = [
  "User Name",
  "Credit Reference",
  "Balance",
  "Client(P/L)",
  "Exposure",
  "Available Balance",
  "U St",
  "B St",
  "Exposure Limit",
  "Default (%)",
  "Account Type",
];

const getColumns = (isListClients: boolean) =>
  isListClients ? EXTENDED_COLUMNS : BASE_COLUMNS;

const mapBaseRow = (account: any) => [
  account.user_name ?? "",
  account.credit_reference ?? 0,
  account.is_active ? "Active" : "Inactive",
  account.is_bet_lock ? "Locked" : "Active",
  account.member_max_exposure_amt ?? 0,
  account.default_percentage ?? 0,
  account.account_type ?? "",
];

const mapExtendedRow = (account: any) => [
  account.user_name ?? "",
  account.credit_reference ?? 0,
  account.balance ?? 0,
  account.client_pnl ?? 0,
  account.user_exposure ?? 0,
  account.total_balance ?? 0,
  account.is_active ? "Active" : "Inactive",
  account.is_bet_lock ? "Locked" : "Active",
  account.member_max_exposure_amt ?? 0,
  account.default_percentage ?? 0,
  account.account_type ?? "",
];

const getRowMapper = (isListClients: boolean) =>
  isListClients ? mapExtendedRow : mapBaseRow;

const buildSummaryRow = (accountList: any[], isListClients: boolean) => {
  const sum = (key: string) =>
    accountList.reduce((s: number, a: any) => s + (a[key] ?? 0), 0);

  if (isListClients) {
    return [
      "",
      sum("credit_reference"),
      sum("balance"),
      sum("client_pnl"),
      "",
      sum("total_balance"),
      "",
      "",
      "",
      "",
      "",
    ];
  }
  return ["", sum("credit_reference"), "", "", "", "", ""];
};

const escapeCsvField = (field: unknown) => {
  const str = String(field);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const exportCSV = (accountList: any[], isListClients: boolean) => {
  const columns = getColumns(isListClients);
  const mapRow = getRowMapper(isListClients);

  const header = columns.map(escapeCsvField).join(",");
  const summary = buildSummaryRow(accountList, isListClients).map(escapeCsvField).join(",");
  const rows = accountList.map((account) =>
    mapRow(account).map(escapeCsvField).join(",")
  );
  const csvContent = [header, summary, ...rows].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "account-list.csv";
  link.click();
  URL.revokeObjectURL(url);
};

const exportPDF = (accountList: any[], isListClients: boolean) => {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ orientation: "landscape", format: [190, 297] });
  const columns = getColumns(isListClients);
  const mapRow = getRowMapper(isListClients);

  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(14);
  doc.text("User List", pageWidth / 2, 16, { align: "center" });

  autoTable(doc, {
    startY: 22,
    head: [columns],
    body: [buildSummaryRow(accountList, isListClients), ...accountList.map(mapRow)],
    styles: { fontSize: 8 },
    headStyles: { fillColor: [25, 103, 210] },
    didParseCell(data) {
      if (data.section === "body" && data.row.index === 0) {
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  doc.save("user-list.pdf");
};

const ExportButtons: React.FC<Props> = ({ accountList, isListClients = false }) => {
  return (
    <div className="d-inline-block mr-2 my-3">
      <button
        type="button"
        className="btn buttons-pdf btn-danger"
        onClick={() => exportPDF(accountList, isListClients)}
      >
        <i className="far fa-file-pdf mr-1" />
        PDF
      </button>{" "}
      <div className="d-inline-block">
        <button
          type="button"
          className="btn buttons-excel btn-success"
          onClick={() => exportCSV(accountList, isListClients)}
        >
          <i className="far fa-file-excel mr-1" />
          Excel
        </button>
      </div>
    </div>
  );
};

export default ExportButtons;
